import { z } from 'zod';

/**
 * THE CAMP TABLET'S MORNING DISPATCH (owner, 2026-10-29) — the wire contract
 * between the tablet and `/api/field/dispatch`.
 *
 * The tablet reads the day's PUBLISHED plan for its own camp (who goes to which
 * site), and writes back who left camp, when, and — once, at the end — why the
 * rest did not go. For logistics; never attendance. See
 * `design/v3/dispatch.states.json` for the screen.
 *
 * Rows follow the other field batch routes: each keyed by a device `uuid`, the
 * time as client wall-clock unix millis with the device's own measured skew,
 * and a per-row result so one bad row never refuses the batch.
 */

export const DEPARTURE_STATUSES = ['TO_GO', 'GONE', 'LATER'] as const;
export type DepartureStatus = (typeof DEPARTURE_STATUSES)[number];

/** NONE_GIVEN is the server's, written at finish — the tablet never sends it. */
export const DEPARTURE_REASONS = ['SICK', 'ABSENT', 'DAY_OFF', 'COMING_LATER', 'NONE_GIVEN'] as const;
export type DepartureReason = (typeof DEPARTURE_REASONS)[number];

const Ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

const base = {
  uuid: z.string().min(8).max(64),
  /** The plan day the tablet was showing, from the read's `day`. */
  day: Ymd,
  ts: z.number().int().positive(), // client wall-clock unix millis
  clockSkewMs: z.number().int().min(-31_536_000_000).max(31_536_000_000).optional(),
  operatorCode: z.string().min(1).max(32).optional(),
};

/**
 * One change on the tablet.
 *
 *   MOVE    the worker's box: GONE (left camp, now), LATER (not in the queue —
 *           no reason asked), TO_GO (an undo).
 *   REASON  why he did not go, picked on the review step.
 *   FINISH  "Save dispatch". The server writes NONE_GIVEN for every planned
 *           worker of this camp not gone and with no reason, so an unpicked
 *           row is saved as a statement rather than as a guess.
 */
export const FieldDepartureEventSchema = z.discriminatedUnion('kind', [
  z.object({ ...base, kind: z.literal('MOVE'), employeeId: z.string().min(1), status: z.enum(DEPARTURE_STATUSES) }),
  z.object({
    ...base,
    kind: z.literal('REASON'),
    employeeId: z.string().min(1),
    reason: z.enum(['SICK', 'ABSENT', 'DAY_OFF', 'COMING_LATER']),
  }),
  z.object({ ...base, kind: z.literal('FINISH') }),
]);
export type FieldDepartureEvent = z.infer<typeof FieldDepartureEventSchema>;

export const FieldDepartureResultSchema = z.object({
  uuid: z.string(),
  status: z.enum(['ACKED', 'DUPLICATE', 'STALE', 'REJECTED', 'ERROR']),
  reason: z.string().optional(),
});
export type FieldDepartureResult = z.infer<typeof FieldDepartureResultSchema>;

/**
 * Whether the list is the plan the office published.
 *
 *   PUBLISHED              it is.
 *   CHANGED_SINCE_PUBLISH  the office edited the day after publishing; the
 *                          rows are the current draft, and the screen says so.
 *   NOT_PUBLISHED          nothing is published for the day: no list at all,
 *                          because an unpublished draft is not an instruction.
 */
export const FIELD_DISPATCH_PLAN_STATES = ['PUBLISHED', 'CHANGED_SINCE_PUBLISH', 'NOT_PUBLISHED'] as const;
export type FieldDispatchPlanState = (typeof FIELD_DISPATCH_PLAN_STATES)[number];

export interface FieldDispatchWorker {
  employeeId: string;
  code: string;
  name: string;
  trade?: string;
  /** Lives in this camp. False: on the plan, sleeps at another camp — still tappable here. */
  here: boolean;
}

export interface FieldDispatchProject {
  id: string;
  code: string | null;
  name: string;
  /** The crew on a two-shift site; null on a site with one. */
  lane: 'DAY' | 'NIGHT' | null;
  workers: FieldDispatchWorker[];
}

export interface FieldDispatchDeparture {
  employeeId: string;
  /** Tapped on THIS camp's tablet. False: another camp recorded it. */
  atThisCamp: boolean;
  status: DepartureStatus;
  departedAt: string | null;
  reason: DepartureReason | null;
}

export interface FieldDispatchDay {
  /** The company-zone day this list is for. */
  day: string;
  /** NOT_A_CAMP: the tablet is bound to a work site, and there is nothing to read. */
  campStatus: 'OK' | 'NOT_A_CAMP';
  camp: { id: string; name: string } | null;
  plan: FieldDispatchPlanState;
  publishedAt: string | null;
  projects: FieldDispatchProject[];
  /** Residents of this camp on approved full-day leave: ON LEAVE in Later, no reason asked. */
  onLeave: (FieldDispatchWorker & { until: string })[];
  departures: FieldDispatchDeparture[];
  finishedAt: string | null;
}
